/**
 * AppointmentPage - Versicherten appointment details
 * Nordic Sanctuary design: Calm, reassuring, clear actions
 */

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  ArrowLeft,
  Video,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
} from "lucide-react";
import { Button } from "@jourfx/ui";
import { ClientLayout } from "../components/layout/ClientLayout";
import { ExaminerCard } from "../components/appointment/ExaminerCard";
import { StatusBadge } from "../components/appointment/StatusBadge";
import { CancelDialog } from "../components/appointment/CancelDialog";
import { useSession } from "../hooks/useSession";
import { useVersichertenSession } from "../hooks/useVersichertenSession";
import { useDateFormat } from "../hooks/useDateFormat";

export function AppointmentPage() {
  const { appointmentId } = useParams({ from: "/appointment/$appointmentId" });
  const navigate = useNavigate();
  const {
    session,
    isValid,
    isLoading: sessionLoading,
    createSession,
  } = useSession(appointmentId);
  const { t } = useTranslation("appointment");
  const { t: tc } = useTranslation("common");
  const { formatWeekday, formatMonthYear, formatTime } = useDateFormat();
  const {
    session: versichertenSession,
    isValid: hasVersichertenSession,
    isLoading: versichertenSessionLoading,
  } = useVersichertenSession();
  const apiRef = api as any;

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isBootstrappingSession, setIsBootstrappingSession] = useState(false);
  const [bootstrapAttempted, setBootstrapAttempted] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const createAppointmentSessionFromVersichertenSession = useMutation(
    apiRef.appointments.index.createAppointmentSessionFromVersichertenSession
  );

  const canBootstrapFromVersichertenSession =
    !!appointmentId &&
    hasVersichertenSession &&
    !!versichertenSession?.sessionToken;

  useEffect(() => {
    if (!appointmentId) return;
    if (sessionLoading || versichertenSessionLoading) return;
    if (isValid || isBootstrappingSession || bootstrapAttempted) return;
    if (!canBootstrapFromVersichertenSession) return;

    setBootstrapAttempted(true);
    setIsBootstrappingSession(true);

    void (async () => {
      try {
        const result = await createAppointmentSessionFromVersichertenSession({
          appointmentId: appointmentId as any,
          sessionToken: versichertenSession!.sessionToken,
        });
        createSession(appointmentId, result.expiresAt, result.sessionToken);
      } catch (error) {
        console.error("Failed to bootstrap appointment session:", error);
      } finally {
        setIsBootstrappingSession(false);
      }
    })();
  }, [
    appointmentId,
    sessionLoading,
    versichertenSessionLoading,
    isValid,
    isBootstrappingSession,
    bootstrapAttempted,
    canBootstrapFromVersichertenSession,
    createAppointmentSessionFromVersichertenSession,
    versichertenSession,
    createSession,
  ]);

  const appointment = useQuery(
    api.appointments.index.getForVersicherter,
    appointmentId && session?.sessionToken
      ? { appointmentId: appointmentId as any, sessionToken: session.sessionToken }
      : "skip"
  );

  const confirmAttendance = useMutation(api.appointments.index.confirmAttendance);
  const cancelAppointment = useMutation(api.appointments.index.cancelByVersicherter);

  const prepWindowOpensAt =
    typeof (appointment as any)?.prepWindowOpensAt === "number"
      ? (appointment as any).prepWindowOpensAt
      : null;
  const prepWindowOpensLabel = useMemo(() => {
    if (typeof prepWindowOpensAt !== "number") return "T-30";
    const opensAt = new Date(prepWindowOpensAt);
    return opensAt.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [prepWindowOpensAt]);
  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);
  const debugMode = testingMode?.enabled ?? false;
  const prepWindowOpen = (appointment as any)?.prepWindowOpen ?? true;
  const effectivePrepWindowOpen =
    debugMode ||
    prepWindowOpen ||
    (typeof prepWindowOpensAt === "number" && nowMs >= prepWindowOpensAt);

  useEffect(() => {
    if (effectivePrepWindowOpen || typeof prepWindowOpensAt !== "number") return;
    const remainingMs = prepWindowOpensAt - nowMs;
    if (remainingMs <= 0) {
      setNowMs(Date.now());
      return;
    }
    const MAX_TIMEOUT_MS = 2_147_483_647;
    const timeout = window.setTimeout(
      () => setNowMs(Date.now()),
      Math.min(remainingMs, MAX_TIMEOUT_MS)
    );
    return () => window.clearTimeout(timeout);
  }, [effectivePrepWindowOpen, nowMs, prepWindowOpensAt]);

  // Check session - redirect to home if invalid (skip in dev mode for testing)
  const shouldRedirectToPortalAccess =
    !sessionLoading &&
    !versichertenSessionLoading &&
    !isValid &&
    !!appointmentId &&
    !import.meta.env.DEV &&
    (!canBootstrapFromVersichertenSession ||
      (bootstrapAttempted && !isBootstrappingSession));

  if (shouldRedirectToPortalAccess) {
    return <Navigate to="/" search={{ id: appointmentId }} />;
  }

  if (isBootstrappingSession || appointment === undefined) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">{t("loading")}</p>
        </div>
      </ClientLayout>
    );
  }
  if (appointment === null) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 text-center">
          <p className="text-client-muted font-medium">{t("notFound")}</p>
        </div>
      </ClientLayout>
    );
  }

  const scheduledDate = new Date(appointment.scheduledAt);
  const CUTOFF_HOURS = 48;
  const isWithinCutoff =
    Date.now() > appointment.scheduledAt - CUTOFF_HOURS * 60 * 60 * 1000;
  const isUpcoming =
    appointment.status === "scheduled" || appointment.status === "confirmed";

  const handleConfirm = async () => {
    if (!appointmentId || !session?.sessionToken) return;
    setIsConfirming(true);
    try {
      await confirmAttendance({
        appointmentId: appointmentId as any,
        sessionToken: session.sessionToken,
      });
    } catch (err) {
      console.error("Confirm failed:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!appointmentId || !session?.sessionToken) return;
    setIsCancelling(true);
    try {
      await cancelAppointment({
        appointmentId: appointmentId as any,
        sessionToken: session.sessionToken,
        reason,
      });
      setShowCancelDialog(false);
    } catch (err) {
      console.error("Cancel failed:", err);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleTechCheck = () => {
    if (!effectivePrepWindowOpen) return;
    navigate({ to: "/tech-check/$appointmentId", params: { appointmentId } });
  };

  const handleJoinVideo = () => {
    navigate({ to: "/video/$appointmentId", params: { appointmentId } });
  };

  // Format date parts
  const dayNumber = scheduledDate.getDate();
  const weekday = formatWeekday(scheduledDate);
  const monthYear = formatMonthYear(scheduledDate);
  const time = formatTime(scheduledDate);

  return (
    <ClientLayout>
      <div className="client-card w-full max-w-md overflow-hidden">
        {hasVersichertenSession && (
          <button
            onClick={() => navigate({ to: "/dashboard" } as any)}
            className="flex items-center gap-1 text-sm text-[var(--stone-600)] hover:text-[var(--sage-600)] transition-colors p-4 pb-0"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Übersicht
          </button>
        )}
        {/* Hero Date Section */}
        <div className="bg-gradient-to-br from-[var(--sage-500)] to-[var(--sage-600)] p-6 text-white text-center relative overflow-hidden">
          {/* Decorative circles */}
          <div
            className="absolute -left-12 -top-12 w-40 h-40 rounded-full bg-white/10"
            aria-hidden="true"
          />
          <div
            className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10"
            aria-hidden="true"
          />

          <div className="relative">
            {/* Status badge */}
            <div className="mb-4">
              <StatusBadge status={appointment.status as any} size="lg" />
            </div>

            {/* Date display */}
            <p className="text-white/80 text-sm font-medium uppercase tracking-wider mb-1">
              {weekday}
            </p>
            <p className="text-6xl font-bold mb-1">{dayNumber}</p>
            <p className="text-white/90 text-lg">{monthYear}</p>

            {/* Time */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
              <Clock className="w-5 h-5" aria-hidden="true" />
              <span className="text-lg font-semibold">{time} {tc("time.oclock")}</span>
              <span className="text-white/70">· {t("details.duration", { duration: appointment.duration })}</span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 space-y-6">
          {/* Examiner */}
          <ExaminerCard
            name={appointment.gutachterName || t("details.examiner")}
            title={t("details.examinerTitle")}
            photoUrl={appointment.gutachterAvatarUrl ?? undefined}
          />

          {/* Actions for upcoming appointments */}
          {isUpcoming && (
            <div className="space-y-3">
              {/* Primary action: Confirm or Tech Check */}
              {appointment.status === "scheduled" ? (
                <Button
                  size="client"
                  className="w-full"
                  onClick={handleConfirm}
                  disabled={isConfirming}
                >
                  {isConfirming ? (
                    <Loader2 className="animate-spin" aria-hidden="true" />
                  ) : (
                    <CheckCircle aria-hidden="true" />
                  )}
                  {t("actions.confirmAttendance")}
                </Button>
              ) : (
                <Button
                  size="client"
                  className="w-full"
                  onClick={handleTechCheck}
                  disabled={!effectivePrepWindowOpen}
                >
                  <Video aria-hidden="true" />
                  {effectivePrepWindowOpen
                    ? t("actions.startTechCheck")
                    : `Technik-Check ab ${prepWindowOpensLabel}`}
                </Button>
              )}

              {/* Secondary action */}
              {appointment.status === "scheduled" && (
                <Button
                  variant="secondary"
                  size="client"
                  className="w-full"
                  onClick={handleTechCheck}
                  disabled={!effectivePrepWindowOpen}
                >
                  <Video aria-hidden="true" />
                  {effectivePrepWindowOpen
                    ? t("actions.startTechCheck")
                    : `Technik-Check ab ${prepWindowOpensLabel}`}
                </Button>
              )}

              {/* Tertiary actions */}
              <div className="pt-2">
                <Button
                  variant="ghost"
                  size="lg"
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <XCircle aria-hidden="true" />
                  {t("actions.cancel")}
                </Button>
                <p className="mt-2 px-4 text-center text-lg text-muted-foreground">
                  {t("rescheduleHelp")}
                </p>
              </div>
            </div>
          )}

          {/* In-progress state: show join video button */}
          {appointment.status === "in_progress" && (
            <div className="space-y-3">
              <Button
                size="client"
                className="w-full"
                onClick={handleJoinVideo}
              >
                <Video aria-hidden="true" />
                Video-Gespräch beitreten
              </Button>
            </div>
          )}

          {/* Cancelled state */}
          {appointment.status === "cancelled" && (
            <div className="text-center p-5 bg-[oklch(0.97_0.02_25)] rounded-2xl border border-[oklch(0.90_0.04_25)]">
              <XCircle
                className="w-10 h-10 mx-auto mb-3 text-[oklch(0.55_0.15_25)]"
                aria-hidden="true"
              />
              <p className="font-semibold text-[oklch(0.40_0.10_25)] mb-1">
                {t("states.cancelled.title")}
              </p>
              <p className="text-sm text-[oklch(0.50_0.08_25)]">
                {t("states.cancelled.message")}
              </p>
            </div>
          )}

          {/* Completed state */}
          {appointment.status === "completed" && (
            <div className="text-center p-5 bg-[var(--stone-50)] rounded-2xl border border-[var(--stone-200)]">
              <CheckCircle
                className="w-10 h-10 mx-auto mb-3 text-[var(--sage-500)]"
                aria-hidden="true"
              />
              <p className="font-semibold text-[var(--stone-700)] mb-1">
                {t("states.completed.title")}
              </p>
              <p className="text-sm text-[var(--stone-500)]">
                {t("states.completed.message")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <CancelDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={handleCancel}
        isWithinCutoff={isWithinCutoff}
        isLoading={isCancelling}
      />
    </ClientLayout>
  );
}
